import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button.js";
import { Input } from "@/components/ui/input.js";
import { Label } from "@/components/ui/label.js";
import { Select } from "@/components/ui/select.js";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog.js";
import {
  presetsForDate,
  buildUpdatePayload,
  validateScopeChange,
  type RecurrenceScope
} from "@/lib/recurrence.js";

/**
 * Recurrence editor matching Google Calendar (REQ-036). Inline dropdown picks a
 * preset; the "Custom…" option exposes the RRULE field. On edit, the scope picker
 * (`this` / `this_and_future` / `all`) gates which fields can change.
 *
 * When `fromInstanceAppointmentId` is supplied (i.e. the editor was opened from a
 * specific appointment instance), all three scopes are available. When opened from
 * the series-list with no instance id, only `all` is allowed and the picker is
 * hidden — this prevents the silent failure where scoped edits ship without the
 * instance id the API requires.
 */
export function RecurrenceEditor({
  open,
  onOpenChange,
  startsAt,
  initialRrule,
  initialDtstart,
  isEdit = false,
  fromInstanceAppointmentId,
  onSubmit
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  startsAt?: string;
  initialRrule?: string;
  initialDtstart?: string;
  isEdit?: boolean;
  /** Appointment id of the instance this edit was launched from. Required for `this` / `this_and_future`. */
  fromInstanceAppointmentId?: string;
  onSubmit: (input: {
    scope?: RecurrenceScope;
    rrule: string;
    dtstart: string;
    until?: string;
    fromInstanceAppointmentId?: string;
  }) => Promise<void>;
}): React.JSX.Element {
  const [rrule, setRrule] = useState(initialRrule ?? "");
  const [dtstart, setDtstart] = useState(initialDtstart ?? startsAt ?? "");
  const [until, setUntil] = useState<string>("");
  // When there is no instance id, only scope=all is valid. Default to it; the picker is hidden.
  const [scope, setScope] = useState<RecurrenceScope>(fromInstanceAppointmentId ? "this" : "all");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const presets = presetsForDate(dtstart || new Date().toISOString());

  useEffect(() => {
    if (open) {
      setRrule(initialRrule ?? "");
      setDtstart(initialDtstart ?? startsAt ?? "");
      setUntil("");
      setScope(fromInstanceAppointmentId ? "this" : "all");
      setError(null);
    }
  }, [open, initialRrule, initialDtstart, startsAt, fromInstanceAppointmentId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit recurrence" : "New recurrence"}</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={async (event) => {
            event.preventDefault();
            // The validator only blocks when the user *changes* the rule on a
            // non-all scope. An unchanged rrule is fine for scope=this /
            // scope=this_and_future — the API only attaches it when scope=all.
            const ruleChanged = rrule !== (initialRrule ?? "");
            const ruleForValidation = ruleChanged ? rrule : "";
            const validation = validateScopeChange({ scope, rrule: ruleForValidation });
            if (!validation.ok) {
              setError(validation.reason);
              return;
            }
            // For scoped edits, build payload upfront so the missing-instance-id
            // failure is caught locally rather than as an API error.
            if (isEdit) {
              try {
                buildUpdatePayload({
                  scope,
                  rrule: ruleForValidation || undefined,
                  fromInstanceAppointmentId
                });
              } catch (err) {
                setError(err instanceof Error ? err.message : String(err));
                return;
              }
            }
            setSubmitting(true);
            try {
              await onSubmit({
                scope: isEdit ? scope : undefined,
                rrule: scope === "all" || !isEdit ? rrule : (ruleChanged ? rrule : ""),
                dtstart,
                until: until || undefined,
                fromInstanceAppointmentId
              });
              onOpenChange(false);
            } catch (err) {
              setError(err instanceof Error ? err.message : String(err));
            } finally {
              setSubmitting(false);
            }
          }}
        >
          <div className="flex flex-col gap-1">
            <Label htmlFor="recurrence-preset">Repeats</Label>
            <Select
              id="recurrence-preset"
              value={presets.find((p) => p.rrule === rrule)?.rrule ?? "__custom"}
              onChange={(e) => {
                if (e.target.value === "__custom") return;
                setRrule(e.target.value);
              }}
            >
              {presets.map((p) => (
                <option key={p.rrule + p.label} value={p.rrule}>
                  {p.label}
                </option>
              ))}
              <option value="__custom">Custom…</option>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="recurrence-rrule">RRULE</Label>
            <Input
              id="recurrence-rrule"
              value={rrule}
              onChange={(e) => setRrule(e.target.value)}
              placeholder="FREQ=WEEKLY;BYDAY=MO"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="recurrence-dtstart">Starts</Label>
              <Input
                id="recurrence-dtstart"
                type="datetime-local"
                value={dtstart ? toLocal(dtstart) : ""}
                onChange={(e) => setDtstart(e.target.value ? new Date(e.target.value).toISOString() : "")}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="recurrence-until">Until (optional)</Label>
              <Input
                id="recurrence-until"
                type="datetime-local"
                value={until ? toLocal(until) : ""}
                onChange={(e) => setUntil(e.target.value ? new Date(e.target.value).toISOString() : "")}
              />
            </div>
          </div>
          {isEdit && fromInstanceAppointmentId && (
            <div className="flex flex-col gap-1">
              <Label htmlFor="recurrence-scope">Scope of change</Label>
              <Select
                id="recurrence-scope"
                value={scope}
                onChange={(e) => setScope(e.target.value as RecurrenceScope)}
              >
                <option value="this">This event only</option>
                <option value="this_and_future">This and following events</option>
                <option value="all">All events</option>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                Editing the rule itself requires "All events".
              </p>
            </div>
          )}
          {isEdit && !fromInstanceAppointmentId && (
            <p className="rounded border bg-muted/40 p-2 text-[11px] text-muted-foreground">
              Editing from the series list applies to all instances. To change a single occurrence,
              open the appointment from the calendar.
            </p>
          )}
          {error && <div className="rounded bg-destructive/10 p-2 text-xs text-destructive-foreground">{error}</div>}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function toLocal(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
